import React, { useState, useEffect, useCallback } from 'react';
import { 
  Save, 
  Eye, 
  Upload, 
  Calendar, 
  MapPin, 
  DollarSign,
  FileText,
  Tag,
  Video,
  Gift,
  Plus,
  Trash2,
  AlertCircle,
  Check
} from 'lucide-react';
import { Input } from './design-system/Input';
import { Button } from './design-system/Button';
import { Card, CardHeader, CardContent } from './design-system/Card';
import { Heading2, Heading3, Body, BodySmall } from './design-system/Typography';
import { ImageUpload } from './ImageUpload';
import { ProjectFormData, ProjectFormErrors, ProjectCategory, ProjectReward } from '../types/project';
import { SupabaseService } from '../services/supabaseService';

interface ProjectFormProps {
  onSave: (projectData: ProjectFormData) => void;
  onPreview: (projectData: ProjectFormData) => void;
  initialData?: Partial<ProjectFormData>;
  isSubmitting?: boolean;
}

const PROJECT_CATEGORIES: { value: ProjectCategory; label: string; description: string }[] = [
  { value: 'technology', label: 'Tecnología', description: 'Apps, software, hardware, innovación tech' },
  { value: 'creative', label: 'Creatividad', description: 'Arte, música, diseño, entretenimiento' },
  { value: 'community', label: 'Comunidad', description: 'Proyectos sociales, iniciativas locales' },
  { value: 'business', label: 'Negocios', description: 'Startups, productos, servicios' },
  { value: 'education', label: 'Educación', description: 'Cursos, libros, material educativo' },
  { value: 'health', label: 'Salud', description: 'Bienestar, medicina, fitness' },
  { value: 'environment', label: 'Medio Ambiente', description: 'Sostenibilidad, ecología, green tech' },
  { value: 'social', label: 'Social', description: 'Causas sociales, ONGs, ayuda humanitaria' },
  { value: 'sports', label: 'Deportes', description: 'Equipos, eventos deportivos, fitness' },
  { value: 'travel', label: 'Viajes', description: 'Turismo, aventuras, experiencias' }
];

const CURRENCIES = [
  { value: 'EUR', label: '€ Euro', symbol: '€' },
  { value: 'USD', label: '$ Dólar', symbol: '$' },
  { value: 'GBP', label: '£ Libra', symbol: '£' }
];

export const ProjectForm: React.FC<ProjectFormProps> = ({
  onSave,
  onPreview,
  initialData,
  isSubmitting = false
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    title: '',
    description: '',
    category: '',
    targetAmount: '',
    currency: 'EUR',
    deadline: '',
    location: '',
    gallery: [],
    videoUrl: '',
    rewards: [],
    ...initialData
  });

  const [errors, setErrors] = useState<ProjectFormErrors>({});
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (formData.title.trim() || formData.description.trim()) {
        saveDraft();
      }
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [formData]);

  const saveDraft = async () => {
    setIsDraftSaving(true);
    try {
      // Save to localStorage as backup
      localStorage.setItem('projectDraft', JSON.stringify({
        ...formData,
        savedAt: new Date().toISOString()
      }));
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setIsDraftSaving(false);
    }
  };

  // Load draft on component mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('projectDraft');
    if (savedDraft && !initialData) {
      try {
        const draft = JSON.parse(savedDraft);
        setFormData(draft);
        setLastSaved(new Date(draft.savedAt));
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, [initialData]);

  const validateField = (name: keyof ProjectFormData, value: any): string | undefined => {
    switch (name) {
      case 'title':
        if (!value.trim()) return 'El título es obligatorio';
        if (value.length > 100) return 'El título no puede exceder 100 caracteres';
        if (value.length < 5) return 'El título debe tener al menos 5 caracteres';
        break;

      case 'description':
        if (!value.trim()) return 'La descripción es obligatoria';
        if (value.length < 200) return 'La descripción debe tener al menos 200 caracteres';
        if (value.length > 1000) return 'La descripción no puede exceder 1000 caracteres';
        break;

      case 'category':
        if (!value) return 'Debes seleccionar una categoría';
        break;

      case 'targetAmount':
        if (!value) return 'El monto objetivo es obligatorio';
        const amount = parseFloat(value);
        if (isNaN(amount) || amount <= 0) return 'Ingresa un monto válido mayor a 0';
        if (amount > 1000000) return 'El monto no puede exceder 1,000,000';
        break;

      case 'deadline':
        if (!value) return 'La fecha límite es obligatoria';
        const deadline = new Date(value);
        const today = new Date();
        const minDate = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days from now
        const maxDate = new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year from now
        
        if (deadline < minDate) return 'La fecha debe ser al menos 7 días en el futuro';
        if (deadline > maxDate) return 'La fecha no puede ser más de 1 año en el futuro';
        break;

      case 'location':
        if (!value.trim()) return 'La ubicación es obligatoria';
        if (value.length > 100) return 'La ubicación no puede exceder 100 caracteres';
        break;

      case 'videoUrl':
        if (value && value.trim()) {
          const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|vimeo\.com\/)/;
          if (!youtubeRegex.test(value)) {
            return 'Ingresa una URL válida de YouTube o Vimeo';
          }
        }
        break;
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: ProjectFormErrors = {};
    let isValid = true;

    // Validate required fields
    const requiredFields: (keyof ProjectFormData)[] = [
      'title', 'description', 'category', 'targetAmount', 'deadline', 'location'
    ];

    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    // Validate optional fields if they have values
    if (formData.videoUrl) {
      const videoError = validateField('videoUrl', formData.videoUrl);
      if (videoError) {
        newErrors.videoUrl = videoError;
        isValid = false;
      }
    }

    // Validate rewards
    if (formData.rewards.length === 0) {
      newErrors.rewards = 'Debes agregar al menos una recompensa para los contribuyentes';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleFieldChange = (field: keyof ProjectFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    // Real-time validation
    const error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleImageSelect = async (file: File, preview: string) => {
    setIsImageUploading(true);
    setImageUploadProgress(0);
    
    // Show preview immediately
    setFormData(prev => ({ ...prev, mainImage: preview }));
    
    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setImageUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 20;
        });
      }, 200);
      
      // Upload to Supabase Storage
      const uploadResult = await SupabaseService.uploadProfileImage(file, `project-${Date.now()}`);
      
      clearInterval(progressInterval);
      setImageUploadProgress(100);
      
      if (uploadResult.success && uploadResult.url) {
        setFormData(prev => ({ ...prev, mainImage: uploadResult.url }));
        URL.revokeObjectURL(preview);
        
        setTimeout(() => {
          setIsImageUploading(false);
          setImageUploadProgress(0);
        }, 1000);
      } else {
        setErrors(prev => ({ ...prev, mainImage: uploadResult.error }));
        setIsImageUploading(false);
        setImageUploadProgress(0);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setErrors(prev => ({ ...prev, mainImage: 'Error al subir la imagen' }));
      setIsImageUploading(false);
      setImageUploadProgress(0);
    }
  };

  const handleImageRemove = async () => {
    const currentImage = formData.mainImage;
    setFormData(prev => ({ ...prev, mainImage: undefined }));
    
    if (currentImage && currentImage.includes('supabase')) {
      try {
        await SupabaseService.deleteProfileImage(currentImage);
      } catch (error) {
        console.warn('Error deleting image from storage:', error);
      }
    } else if (currentImage && currentImage.startsWith('blob:')) {
      URL.revokeObjectURL(currentImage);
    }
  };

  const addReward = () => {
    const newReward: ProjectReward = {
      id: `reward-${Date.now()}`,
      title: '',
      description: '',
      amount: 0,
      estimatedDelivery: '',
      currentBackers: 0
    };
    setFormData(prev => ({
      ...prev,
      rewards: [...prev.rewards, newReward]
    }));
  };

  const updateReward = (index: number, field: keyof ProjectReward, value: any) => {
    setFormData(prev => ({
      ...prev,
      rewards: prev.rewards.map((reward, i) => 
        i === index ? { ...reward, [field]: value } : reward
      )
    }));
  };

  const removeReward = (index: number) => {
    setFormData(prev => ({
      ...prev,
      rewards: prev.rewards.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handlePreview = () => {
    if (validateForm()) {
      onPreview(formData);
    }
  };

  // Get minimum date (7 days from now)
  const getMinDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };

  // Get maximum date (1 year from now)
  const getMaxDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center">
        <Heading2>Crear Nuevo Proyecto</Heading2>
        <Body className="mt-2 text-gray-600">
          Completa la información de tu proyecto para comenzar a recaudar fondos
        </Body>
        
        {/* Auto-save indicator */}
        <div className="flex items-center justify-center gap-2 mt-4">
          {isDraftSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <BodySmall className="text-blue-600">Guardando borrador...</BodySmall>
            </>
          ) : lastSaved ? (
            <>
              <Check className="w-4 h-4 text-green-500" />
              <BodySmall className="text-green-600">
                Borrador guardado a las {lastSaved.toLocaleTimeString()}
              </BodySmall>
            </>
          ) : null}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* Basic Information */}
        <Card variant="elevated" padding="lg">
          <CardHeader>
            <Heading3>Información Básica</Heading3>
            <BodySmall>Datos esenciales de tu proyecto</BodySmall>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              
              {/* Title */}
              <div>
                <Input
                  label="Título del Proyecto *"
                  type="text"
                  placeholder="Un título claro y atractivo para tu proyecto"
                  value={formData.title}
                  onChange={(e) => handleFieldChange('title', e.target.value)}
                  error={errors.title}
                  icon={FileText}
                  required
                />
                <div className="flex justify-end mt-1">
                  <BodySmall className={`${formData.title.length > 100 ? 'text-red-500' : 'text-gray-500'}`}>
                    {formData.title.length}/100 caracteres
                  </BodySmall>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción Detallada *
                </label>
                <textarea
                  rows={6}
                  placeholder="Describe tu proyecto en detalle. Explica qué es, por qué es importante, cómo usarás los fondos y qué impacto tendrá..."
                  value={formData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  className={`
                    w-full px-4 py-3 text-sm text-gray-900 rounded-lg border resize-none
                    ${errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    placeholder:text-gray-500
                  `}
                  maxLength={1000}
                />
                <div className="flex justify-between items-center mt-2">
                  {errors.description && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.description}
                    </p>
                  )}
                  <BodySmall className={`ml-auto ${
                    formData.description.length < 200 ? 'text-red-500' : 
                    formData.description.length > 1000 ? 'text-red-500' : 'text-gray-500'
                  }`}>
                    {formData.description.length}/1000 caracteres (mínimo 200)
                  </BodySmall>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Categoría del Proyecto *
                </label>
                <div className="grid md:grid-cols-2 gap-3">
                  {PROJECT_CATEGORIES.map((category) => (
                    <div
                      key={category.value}
                      onClick={() => handleFieldChange('category', category.value)}
                      className={`
                        p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
                        ${formData.category === category.value 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }
                      `}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`
                          w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5
                          ${formData.category === category.value 
                            ? 'border-blue-500 bg-blue-500' 
                            : 'border-gray-300'
                          }
                        `}>
                          {formData.category === category.value && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <div>
                          <h4 className={`font-medium ${
                            formData.category === category.value ? 'text-blue-700' : 'text-gray-700'
                          }`}>
                            {category.label}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {category.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {errors.category && (
                  <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.category}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Information */}
        <Card variant="elevated" padding="lg">
          <CardHeader>
            <Heading3>Información Financiera</Heading3>
            <BodySmall>Monto objetivo y fecha límite</BodySmall>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              
              {/* Target Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto Objetivo *
                </label>
                <div className="relative">
                  <select
                    value={formData.currency}
                    onChange={(e) => handleFieldChange('currency', e.target.value)}
                    className="absolute left-3 top-3 bg-transparent border-none text-gray-600 text-sm focus:outline-none"
                  >
                    {CURRENCIES.map(currency => (
                      <option key={currency.value} value={currency.value}>
                        {currency.symbol}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="10000"
                    value={formData.targetAmount}
                    onChange={(e) => handleFieldChange('targetAmount', e.target.value)}
                    className={`
                      w-full pl-12 pr-4 py-3 text-sm text-gray-900 rounded-lg border
                      ${errors.targetAmount ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    `}
                    min="1"
                    max="1000000"
                    step="1"
                  />
                  <DollarSign className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                </div>
                {errors.targetAmount && (
                  <p className="text-sm text-red-600 mt-1">{errors.targetAmount}</p>
                )}
                <BodySmall className="text-gray-500 mt-1">
                  Monto total que necesitas recaudar
                </BodySmall>
              </div>

              {/* Deadline */}
              <div>
                <Input
                  label="Fecha Límite *"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => handleFieldChange('deadline', e.target.value)}
                  error={errors.deadline}
                  icon={Calendar}
                  min={getMinDate()}
                  max={getMaxDate()}
                  required
                  hint="Entre 7 días y 1 año desde hoy"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card variant="elevated" padding="lg">
          <CardHeader>
            <Heading3>Ubicación</Heading3>
            <BodySmall>¿Dónde se desarrollará tu proyecto?</BodySmall>
          </CardHeader>
          <CardContent>
            <Input
              label="Ubicación Geográfica *"
              type="text"
              placeholder="Ciudad, País"
              value={formData.location}
              onChange={(e) => handleFieldChange('location', e.target.value)}
              error={errors.location}
              icon={MapPin}
              required
              hint="Ej: Madrid, España"
            />
          </CardContent>
        </Card>

        {/* Media */}
        <Card variant="elevated" padding="lg">
          <CardHeader>
            <Heading3>Contenido Multimedia</Heading3>
            <BodySmall>Imágenes y videos para mostrar tu proyecto</BodySmall>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              
              {/* Main Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Imagen Principal del Proyecto *
                </label>
                <ImageUpload
                  onImageSelect={handleImageSelect}
                  onImageRemove={handleImageRemove}
                  currentImage={formData.mainImage}
                  isUploading={isImageUploading}
                  uploadProgress={imageUploadProgress}
                  maxSizeBytes={5 * 1024 * 1024}
                  acceptedFormats={['image/jpeg', 'image/jpg', 'image/png']}
                />
                {errors.mainImage && (
                  <p className="text-sm text-red-600 mt-2">{errors.mainImage}</p>
                )}
              </div>

              {/* Video URL */}
              <Input
                label="Video Explicativo (Opcional)"
                type="url"
                placeholder="https://youtube.com/watch?v=... o https://vimeo.com/..."
                value={formData.videoUrl}
                onChange={(e) => handleFieldChange('videoUrl', e.target.value)}
                error={errors.videoUrl}
                icon={Video}
                hint="URL de YouTube o Vimeo para explicar tu proyecto"
              />
            </div>
          </CardContent>
        </Card>

        {/* Rewards */}
        <Card variant="elevated" padding="lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <Heading3>Recompensas para Contribuyentes</Heading3>
                <BodySmall>Define qué recibirán las personas que apoyen tu proyecto</BodySmall>
              </div>
              <Button
                type="button"
                onClick={addReward}
                variant="secondary"
                size="sm"
                icon={Plus}
              >
                Agregar Recompensa
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {formData.rewards.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <Gift className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <Body className="text-gray-600 mb-4">
                  No has agregado recompensas aún
                </Body>
                <Button
                  type="button"
                  onClick={addReward}
                  variant="primary"
                  size="sm"
                  icon={Plus}
                >
                  Agregar Primera Recompensa
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.rewards.map((reward, index) => (
                  <div key={reward.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-700">Recompensa #{index + 1}</h4>
                      <Button
                        type="button"
                        onClick={() => removeReward(index)}
                        variant="ghost"
                        size="sm"
                        icon={Trash2}
                        className="text-red-500 hover:text-red-700"
                      />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        label="Título de la Recompensa"
                        type="text"
                        placeholder="Ej: Acceso Temprano"
                        value={reward.title}
                        onChange={(e) => updateReward(index, 'title', e.target.value)}
                        required
                      />
                      
                      <Input
                        label="Monto Mínimo"
                        type="number"
                        placeholder="25"
                        value={reward.amount || ''}
                        onChange={(e) => updateReward(index, 'amount', parseFloat(e.target.value) || 0)}
                        min="1"
                        required
                      />
                    </div>
                    
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descripción de la Recompensa
                      </label>
                      <textarea
                        rows={3}
                        placeholder="Describe qué recibirá el contribuyente..."
                        value={reward.description}
                        onChange={(e) => updateReward(index, 'description', e.target.value)}
                        className="w-full px-4 py-3 text-sm text-gray-900 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>
                    
                    <div className="mt-4">
                      <Input
                        label="Entrega Estimada"
                        type="text"
                        placeholder="Ej: 2 meses después del financiamiento"
                        value={reward.estimatedDelivery}
                        onChange={(e) => updateReward(index, 'estimatedDelivery', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {errors.rewards && (
              <p className="text-sm text-red-600 mt-4 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.rewards}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-end">
          <Button
            type="button"
            onClick={handlePreview}
            variant="secondary"
            size="lg"
            icon={Eye}
            disabled={isSubmitting}
          >
            Vista Previa
          </Button>
          
          <Button
            type="submit"
            variant="primary"
            size="lg"
            icon={Save}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Publicando...' : 'Publicar Proyecto'}
          </Button>
        </div>
      </form>
    </div>
  );
};